import {
  GetTelegramResponseType,
  ConnectTelegramResponseType,
  GetWalletPositionsResponseType,
  PositionsGetGlobalInfoRequestBodyType,
  PositionsGetGlobalInfoResponseType,
  PositionsGetInfoBatchRequestBodyType,
  PositionsGetInfoBatchResponseType,
  ConnectTelegramBodyType,
  DisconnectTelegramResponseType,
  PositionsGetGlobalMetricsResponseType,
  PositionsGetMetricsResponseType,
  GetActivityHistoryResponseType,
  GetVaultMetricsResponseType,
  GetVaultMetricsHistoryIntervalType,
  GetVaultMetricsHistoryResponseType,
  PositionsGetExistingPositionsIdsResponseType,
  PositionsGetMetricsHistoryResponseType,
  IsTransactionIndexedResponseType,
  GetTermsAcceptanceResponseType,
  AcceptTermsResponseType,
} from './types'
import {
  PositionsResponseType,
  OwnersResponseType,
  OrderType,
  PositionsSortByType,
  TimeFilterType,
  PositionsStatusFilterType,
  OwnersSortByType,
  IsStablePairFilterType,
} from './types/leaderboard'

export const BACKEND_CLIENT_BASE_URL = 'https://api.kai.finance'

export class ApiResponseError extends Error {
  public readonly status: number
  public readonly statusText: string
  public readonly data?: unknown

  constructor(status: number, statusText: string, message?: string, data?: unknown) {
    super(message || `HTTP error! status: ${status}`)
    this.name = 'ApiResponseError'
    this.status = status
    this.statusText = statusText
    this.data = data
  }

  isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  isServerError(): boolean {
    return this.status >= 500 && this.status < 600
  }
}

export class BackendClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private createApiError(status: number, statusText: string, data?: unknown): ApiResponseError {
    return new ApiResponseError(status, statusText, undefined, data)
  }

  async getWalletPositions(walletAddress: string) {
    try {
      const response = await fetch(`${this.baseUrl}/positions/getWalletPositions/${walletAddress}`)

      if (!response.ok) {
        const data = await response.json().catch(() => undefined)
        throw this.createApiError(response.status, response.statusText, data)
      }

      return (await response.json()) as GetWalletPositionsResponseType
    } catch (error) {
      console.error('Error fetching wallet positions:', error)
      throw error
    }
  }

  async positionsGetInfoBatch(body: PositionsGetInfoBatchRequestBodyType) {
    try {
      const response = await fetch(`${this.baseUrl}/positions/getInfoBatch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => undefined)
        throw this.createApiError(response.status, response.statusText, data)
      }

      return (await response.json()) as PositionsGetInfoBatchResponseType
    } catch (error) {
      console.error('Error fetching position infos:', error)
    }
    return null
  }

  async positionsGetGlobalInfo(body: PositionsGetGlobalInfoRequestBodyType) {
    try {
      const response = await fetch(`${this.baseUrl}/positions/getGlobalInfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => undefined)
        throw this.createApiError(response.status, response.statusText, data)
      }

      return (await response.json()) as PositionsGetGlobalInfoResponseType
    } catch (error) {
      console.error('Error fetching global info:', error)
    }
    return null
  }

  async positionsGetGlobalMetrics() {
    try {
      const response = await fetch(`${this.baseUrl}/positions/getGlobalMetrics`, {
        method: 'GET',
      })

      if (!response.ok) {
        const data = await response.json().catch(() => undefined)
        throw this.createApiError(response.status, response.statusText, data)
      }

      return (await response.json()) as PositionsGetGlobalMetricsResponseType
    } catch (error) {
      console.error('Error fetching global metrics:', error)
    }
    return null
  }

  async getTelegramConnection(walletAddress: string): Promise<GetTelegramResponseType> {
    const response = await fetch(`${this.baseUrl}/telegram/${encodeURIComponent(walletAddress)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const data = await response.json()

    if (!response.ok) {
      throw this.createApiError(response.status, response.statusText, data)
    }

    return data as GetTelegramResponseType
  }

  async verifyTelegram(
    walletAddress: string,
    message: string,
    signature: string
  ): Promise<ConnectTelegramResponseType> {
    const body: ConnectTelegramBodyType = {
      walletAddress,
      message,
      signature,
    }

    const response = await fetch(`${this.baseUrl}/telegram`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    const data = await response.json()

    if (!response.ok) {
      throw this.createApiError(response.status, response.statusText, data)
    }

    return data as ConnectTelegramResponseType
  }

  async disconnectTelegram(
    walletAddress: string,
    message: string,
    signature: string
  ): Promise<DisconnectTelegramResponseType> {
    const response = await fetch(`${this.baseUrl}/telegram/${encodeURIComponent(walletAddress)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message, signature }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw this.createApiError(response.status, response.statusText, data)
    }

    return data as DisconnectTelegramResponseType
  }

  async getPositionMetrics(positionId: string): Promise<PositionsGetMetricsResponseType> {
    const response = await fetch(`${this.baseUrl}/positions/getMetrics/${positionId}`)

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as PositionsGetMetricsResponseType
  }

  async getSavActivityHistoryForWallet(
    walletAddress: string,
    vaultId: string
  ): Promise<GetActivityHistoryResponseType> {
    const response = await fetch(`${this.baseUrl}/sav/getActivityHistory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ walletAddress, vaultId }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as GetActivityHistoryResponseType
  }

  async getSavMetrics(): Promise<GetVaultMetricsResponseType> {
    const response = await fetch(`${this.baseUrl}/sav/getVaultMetrics`)

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as GetVaultMetricsResponseType
  }

  async getSavMetricsHistory(
    vaultId: string,
    interval: GetVaultMetricsHistoryIntervalType
  ): Promise<GetVaultMetricsHistoryResponseType> {
    const response = await fetch(`${this.baseUrl}/sav/getVaultMetricsHistory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ vaultId, interval }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as GetVaultMetricsHistoryResponseType
  }

  async getExistingPositions(): Promise<string[]> {
    const response = await fetch(`${this.baseUrl}/positions/existing`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as PositionsGetExistingPositionsIdsResponseType
  }

  async positionsGetMetricsHistory(
    positionId: string
  ): Promise<PositionsGetMetricsHistoryResponseType> {
    const response = await fetch(`${this.baseUrl}/positions/getMetricsHistory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ positionId }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as PositionsGetMetricsHistoryResponseType
  }

  async leaderboardGetPositions(
    sortBy: PositionsSortByType,
    order: OrderType,
    timeFilter: TimeFilterType,
    statusFilter: PositionsStatusFilterType,
    isStablePairFilter: IsStablePairFilterType
  ): Promise<PositionsResponseType> {
    const params = new URLSearchParams()
    if (sortBy) params.append('sortBy', sortBy)
    if (order) params.append('order', order)
    if (timeFilter) params.append('timeFilter', timeFilter)
    if (statusFilter) params.append('statusFilter', statusFilter)
    if (isStablePairFilter !== undefined && isStablePairFilter !== null) {
      params.append('isStablePairFilter', isStablePairFilter.toString())
    }

    const url = `${this.baseUrl}/leaderboard/getPositions?${params}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as PositionsResponseType
  }

  async leaderboardGetOwners(
    sortBy: OwnersSortByType,
    order: OrderType,
    timeFilter: TimeFilterType
  ): Promise<OwnersResponseType> {
    const params = new URLSearchParams()
    if (sortBy) params.append('sortBy', sortBy)
    if (order) params.append('order', order)
    if (timeFilter) params.append('timeFilter', timeFilter)

    const url = `${this.baseUrl}/leaderboard/getOwners?${params}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as OwnersResponseType
  }

  async getTermsAcceptance(walletAddress: string): Promise<GetTermsAcceptanceResponseType> {
    const response = await fetch(`${this.baseUrl}/terms/${encodeURIComponent(walletAddress)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as GetTermsAcceptanceResponseType
  }

  async acceptTerms(walletAddress: string): Promise<AcceptTermsResponseType> {
    const response = await fetch(`${this.baseUrl}/terms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ walletAddress }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => undefined)
      throw this.createApiError(response.status, response.statusText, data)
    }

    return (await response.json()) as AcceptTermsResponseType
  }

  async isTransactionIndexed(digest: string): Promise<boolean> {
    const response = await fetch(
      `${this.baseUrl}/status/transaction-indexed/${encodeURIComponent(digest)}`
    )
    if (!response.ok) {
      return false
    }
    const data = (await response.json()) as IsTransactionIndexedResponseType
    return data.indexed
  }
}
